/**
 * Small helpers shared between Fee Notice Queue views. Lives outside the
 * view components so the pure logic can be unit-tested without booting
 * Apryse / WebViewer / Redux — same rationale as MailIntake/utils.js.
 */
import { getPresignedDocumentUrl } from "../../components/Document/DocumentUtils";
import { fetchIntakeItemAPI } from "../../service/mailIntake";

export const STATUS_LABELS = {
  extracting: "Extracting",
  ready: "Ready for review",
  confirmed: "Confirmed",
  dismissed: "Dismissed",
};

export const SF_WRITE_STATUS_LABELS = {
  not_attempted: "Not written yet",
  disabled: "Write disabled",
  succeeded: "Written to Salesforce",
  failed: "Salesforce write failed",
  // No-fee confirms record the fact in DMS only; there are no dollars,
  // so there is deliberately no Salesforce Resolution.
  skipped: "No fee awarded — DMS only",
};

// Statuses a reviewer can open from the queue list. "extracting" letters
// have no extracted_data yet (mirrors mail intake's "uploading" — nothing
// to review), and confirmed/dismissed are terminal (the claim endpoint
// would 409 on them anyway).
export const CLICKABLE_STATUSES = new Set(["ready"]);

/**
 * Resolve a loadable document source for a fee-notice queue item, in the
 * same `{ url, isDirect, withCredentials, fileName? }` shape
 * `useDocumentLoader`'s `resolveDocumentUrl` returns — this is a
 * `ReadOnlyDocumentViewer` `resolveSource` override (see that
 * component), not a bespoke loader: the shared viewer's retry/timeout/
 * error-surfacing logic in `loadDocument()` applies here exactly as it
 * does everywhere else in DMS.
 *
 * Assigned/sorted letters carry `document_id` — resolved the same
 * presigned-first way the Inbox viewer resolves any document id (in
 * fact this delegates straight to `getPresignedDocumentUrl`). Swept
 * copies of letters still sitting in the unsorted mail queue carry only
 * `intake_item_id` (per the frontend handoff: "view the letter through
 * the existing mail-intake PDF route when document_id is null") —
 * resolved via the mail-intake item detail endpoint's `view_url`
 * (mail-connector PDFs live in a bucket the streaming service can't
 * serve).
 *
 * Once a sorter assigns or discards that mail, the backend stops minting
 * `view_url` (MailIntakeItemSerializer.get_view_url returns None for
 * ASSIGNED/DELETED: "the DMS document record takes over as the source of
 * truth"). Those letters resolve through the intake row's own `document`
 * id — via `getPresignedDocumentUrl`, so they take the same presigned-S3
 * path as every other viewer and inherit its credentialed stream
 * fallback. Verified on production 2026-08-19: 84 of the first 100
 * active queue rows are in exactly this state.
 *
 * Never throws: any failure resolves to `null` so the shared viewer's
 * own resolveDocumentUrl fallback (and its LOAD_ERROR handling) still
 * runs, rather than leaving an unhandled rejection that blanks the pane
 * silently. Letters are always PDFs, so the WebViewer extension/
 * streaming decision is left to `loadDocument()`'s own defaults.
 */
export const resolveFeeNoticeDocumentSource = async (item) => {
  if (!item) return null;

  try {
    if (item.document_id) {
      const resolved = await getPresignedDocumentUrl(item.document_id);
      return resolved?.url ? resolved : null;
    }

    if (item.intake_item_id) {
      const response = await fetchIntakeItemAPI(item.intake_item_id);
      const intake = response?.data;
      if (intake?.view_url) {
        return { url: intake.view_url, isDirect: true, withCredentials: false };
      }
      if (intake?.document) {
        const resolved = await getPresignedDocumentUrl(intake.document);
        return resolved?.url ? resolved : null;
      }
    }
  } catch (err) {
    console.warn(
      "Failed to resolve fee-notice document source:",
      err?.message
    );
  }

  return null;
};

export const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatIsoDate = (value) => {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatMoney = (value) => {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Present the webhook-maintained Payload paid state (`payment_status`
 * on queue items and the invoice payload: status / total_paid /
 * amount_due as Payload's invoice carries them). Returns null before
 * any payment event — the UI shows nothing rather than a speculative
 * "unpaid" on letters that were never sent. Otherwise
 * `{ tone, label, detail }` where tone picks the pill color:
 * paid → green, partial → amber, problem → red, pending → blue.
 */
export const describePaymentStatus = (paymentStatus) => {
  const raw = String(paymentStatus?.status || "")
    .toLowerCase()
    .trim();
  if (!raw) return null;

  const paid = Number.parseFloat(paymentStatus.total_paid);
  const due = Number.parseFloat(paymentStatus.amount_due);
  const hasPaid = Number.isFinite(paid) && paid > 0;
  const hasDue = Number.isFinite(due) && due > 0;

  // Problem statuses win over the amounts: a reversal or chargeback
  // can still carry a settled total_paid, and that row must read red,
  // not green (Bugbot, PR #534).
  if (
    ["declined", "voided", "reversed", "rejected", "chargeback"].some(
      (word) => raw.includes(word)
    )
  ) {
    return {
      tone: "problem",
      label: `Payment ${raw.replace(/_/g, " ")}`,
      detail: hasDue ? `${formatMoney(due)} due` : "",
    };
  }
  if (raw === "paid" || (hasPaid && Number.isFinite(due) && !hasDue)) {
    return {
      tone: "paid",
      label: "Paid",
      detail: hasPaid ? `${formatMoney(paid)} received` : "",
    };
  }
  if (hasPaid && hasDue) {
    return {
      tone: "partial",
      label: "Partially paid",
      detail: `${formatMoney(due)} still due`,
    };
  }
  // "unpaid" (a processing event landed but no settlement yet) and
  // anything Payload adds later.
  return {
    tone: "pending",
    label: raw === "unpaid" ? "Awaiting payment" : raw.replace(/_/g, " "),
    detail: hasDue ? `${formatMoney(due)} due` : "",
  };
};

/** Human label for a duplicate flag's source record — always an
 * already-entered record (SF Resolution or confirmed queue entry).
 * Terse record references belong in the dismiss-reason audit trail
 * only; the banner shows `duplicateFlagMessage` instead. */
export const describeDuplicateFlag = (flag) => {
  if (!flag) return "";
  if (flag.kind === "salesforce") {
    return `Existing Salesforce resolution ${
      flag.name || flag.sf_id || ""
    }`.trim();
  }
  return `Queue entry #${flag.item_id} (already confirmed)`;
};

/** "hypertension (10%, effective Feb 26, 2026); tinnitus" — the
 * entered record's issues, one clause each. */
export const summarizeIssues = (issues) => {
  if (!Array.isArray(issues) || issues.length === 0) return "";
  return issues
    .map((issue) => {
      const name = issue.condition || issue.text || "unnamed issue";
      const details = [];
      if (issue.percent != null) details.push(`${issue.percent}%`);
      if (issue.effective_date)
        details.push(`effective ${formatIsoDate(issue.effective_date)}`);
      return details.length ? `${name} (${details.join(", ")})` : name;
    })
    .join("; ");
};

/**
 * The banner sentence, in the reviewer's terms (Matthew, 2026-07-26:
 * "queue entry #38 (already confirmed)" is meaningless — say when the
 * Resolution was entered, the letter date, the award, and the
 * issues). Facts the record doesn't carry are simply omitted.
 */
export const duplicateFlagMessage = (flag) => {
  if (!flag) return "";
  const enteredOn =
    flag.kind === "salesforce" ? flag.created_at : flag.entered_on;
  let sentence = `This matter has a Resolution${
    flag.kind === "salesforce" ? " in Salesforce" : ""
  }`;
  if (enteredOn) {
    // SF CreatedDate is a full timestamp; keep the date part only.
    sentence += ` entered on ${formatIsoDate(String(enteredOn).slice(0, 10))}`;
  }
  if (flag.date) {
    sentence +=
      flag.kind === "queue"
        ? ` for a SOC/FAN dated ${formatIsoDate(flag.date)}`
        : ` with resolution date ${formatIsoDate(flag.date)}`;
  }
  if (flag.amount != null) {
    sentence += ` ${flag.date && flag.kind === "salesforce" ? "and" : "with"} an award of ${formatMoney(flag.amount)}`;
  }
  const issues = summarizeIssues(flag.issues);
  if (issues) sentence += ` for issues: ${issues}`;
  return `${sentence}.`;
};

export const buildDuplicateDismissReason = (flag) =>
  `Duplicate of ${describeDuplicateFlag(flag)} — matched on ${
    Array.isArray(flag?.matched_on) ? flag.matched_on.join(" & ") : "date/amount"
  }.`;

/**
 * Present the hybrid detection verdict (`item.classification` from the
 * backend: deterministic title match + LLM second opinion). Returns
 * null for items that predate the classifier (empty blob), otherwise
 * `{ phraseLabel, llmLabel, reason, warning }` where `warning` is
 * non-null only when the two signals disagreed — those letters carry a
 * "check detection" flag instead of being silently trusted/dropped.
 */
export const describeDetection = (classification) => {
  if (!classification) return null;
  const { phrase_match: phraseMatch, llm, disagreement } = classification;
  if (llm == null && !phraseMatch) return null;

  const docType = (llm?.doc_type || "").replace(/_/g, " ").trim();
  let warning = null;
  if (disagreement && llm) {
    warning = llm.is_fee_notice
      ? "Flagged by the AI reader alone — the SOC/FAN title phrase was " +
        "not found in this letter. Double-check it really is a fee notice."
      : `The AI reader thinks this letter is ${
          docType ? `a ${docType}` : "not a fee notice"
        }, not a SOC/FAN. If it's right, dismiss this letter.`;
  }
  return {
    phraseLabel: phraseMatch
      ? "SOC/FAN title found"
      : "No SOC/FAN title",
    llmLabel: llm
      ? llm.is_fee_notice
        ? "AI reader: fee notice"
        : `AI reader: ${docType || "not a fee notice"}`
      : "AI reader: no verdict",
    reason: llm?.reason || "",
    warning,
  };
};
